/**
 * ポーリング結果の変化を見て、フィードのイベントを作るフック（タスク54）。
 *
 * - usePins（30秒）と usePosts（60秒）の data が変わるたびに、それぞれ前回との差分を取る。
 * - 初回は基準を作るだけでイベントは出さない。
 * - 別のアプリから戻った直後（タブ再表示）は、前回と比べたうえで直近 RESUME_WINDOW_SEC 以内の出来事だけ出す（タスク54.5）。
 *   ポーリングは非表示中に止まるので、差分をそのまま出すと長く離れていた後に古い変化がまとめて出る。
 *   かといって捨てると、携帯で Telegram などに切り替えている間の出来事が全部消える（実機で判明）。
 *   時刻はサーバー時刻（server_time）を基準にする（端末の時計ずれの影響を受けない）。
 * - フィードが OFF の間も基準は更新し続ける（呼び出し側が捨てる）。ON に戻した瞬間にまとめて出ないように。
 */
import { useEffect, useRef } from 'react';
import type { LocationsResponse, PostsResponse } from '../api/client';
import {
  detectLocationEvents,
  detectPostEvents,
  RESUME_WINDOW_SEC,
  type FeedEvent,
  type LocationState,
} from './detectEvents';

export function useActivityFeed(
  pins: LocationsResponse | null,
  posts: PostsResponse | null,
  onEvents: (events: FeedEvent[]) => void,
): void {
  const locStateRef = useRef<LocationState | null>(null);
  const postIdsRef = useRef<Set<string> | null>(null);
  /** タブ再表示の後、まだ次の data を判定していない（pins・posts は周期が別なので別々に持つ）。 */
  const resumePinsRef = useRef(false);
  const resumePostsRef = useRef(false);
  // 最新のコールバックを保持（data の変化だけで差分を取るため、依存配列に入れない）
  const onEventsRef = useRef(onEvents);
  onEventsRef.current = onEvents;

  // タブ再表示 → 次に届いた data は直近の出来事だけ出す
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        resumePinsRef.current = true;
        resumePostsRef.current = true;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    if (!pins) {
      return;
    }
    const minUpdatedAt = resumePinsRef.current
      ? pins.server_time - RESUME_WINDOW_SEC
      : undefined;
    resumePinsRef.current = false;
    const { events, next } = detectLocationEvents(
      locStateRef.current,
      pins.users,
      Date.now(),
      minUpdatedAt,
    );
    locStateRef.current = next;
    if (events.length > 0) {
      onEventsRef.current(events);
    }
  }, [pins]);

  useEffect(() => {
    if (!posts) {
      return;
    }
    const minCreatedAt = resumePostsRef.current
      ? posts.server_time - RESUME_WINDOW_SEC
      : undefined;
    resumePostsRef.current = false;
    const { events, next } = detectPostEvents(
      postIdsRef.current,
      posts.posts,
      minCreatedAt,
    );
    postIdsRef.current = next;
    if (events.length > 0) {
      onEventsRef.current(events);
    }
  }, [posts]);
}
